import React from "react";
import QRCode from "qrcode";
import { QRCodeCanvas } from "qrcode.react";
import { Printer } from "lucide-react";

const NCC_LOGO = "/ncc-limited-logo.png";

const escHtml = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Print ONLY the digital ID card in a clean popup window (no page chrome).
export async function printDigitalIdCard(d) {
  const verifyUrl = `${window.location.origin}/workman-verify/${d.id}`;
  let qr = "";
  try { qr = await QRCode.toDataURL(verifyUrl, { margin: 0, width: 180 }); } catch { /* no qr */ }
  const rows = [
    d.trade && `<div>Trade: <b>${escHtml(d.trade)}</b></div>`,
    d.mobile && `<div>Mob: <b>${escHtml(d.mobile)}</b></div>`,
    d.contractorName && `<div>Contractor: <b>${escHtml(d.contractorName)}</b></div>`,
    d.uan && `<div>UAN: <b>${escHtml(d.uan)}</b></div>`,
    d.esic && `<div>ESIC: <b>${escHtml(d.esic)}</b></div>`,
    `<div>Blood Group: <b>${escHtml(d.bloodGroup || "—")}</b></div>`,
  ].filter(Boolean).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Digital ID Card - ${escHtml(d.name)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; display: flex; justify-content: center; padding-top: 10mm; }
  .card { width: 92mm; border: 1.2mm solid #0a2756; background: #fff; }
  .hd { display: flex; align-items: center; gap: 2.5mm; background: #0a2756; border-bottom: 1.2mm solid #f0b429; padding: 2.5mm 3mm; }
  .hd img { height: 9mm; width: auto; background: #fff; border-radius: 1mm; padding: 0.6mm; }
  .hd .tt { color: #fff; font-size: 9pt; font-weight: 900; line-height: 1.2; }
  .hd .tt small { display: block; color: #b9c8e8; font-weight: 400; font-size: 6.5pt; font-family: monospace; letter-spacing: 0.5px; }
  .hd .site { margin-left: auto; text-align: right; color: #f0b429; font-size: 7pt; font-weight: 800; line-height: 1.25; max-width: 30mm; }
  .bd { display: flex; gap: 3mm; padding: 3mm; }
  .ph { width: 20mm; height: 25mm; border: 0.3mm solid #999; object-fit: cover; display: block; }
  .ph-ph { width: 20mm; height: 25mm; border: 0.3mm solid #999; background: #eef1f7; display: grid; place-items: center; font-size: 16pt; font-weight: 900; color: #9aa4bd; }
  .info { font-size: 8pt; line-height: 1.55; min-width: 0; }
  .info .nm { font-size: 10.5pt; font-weight: 900; color: #111; }
  .info .idno { font-family: monospace; color: #555; }
  .ft { display: flex; align-items: center; justify-content: space-between; border-top: 0.3mm solid #ddd; padding: 2.5mm 3mm; }
  .ft img { width: 17mm; height: 17mm; display: block; }
  .ft .meta { text-align: right; font-family: monospace; font-size: 7pt; color: #555; line-height: 1.6; }
  .ok { color: #047857; font-weight: 800; } .no { color: #dc2626; font-weight: 800; }
</style></head><body>
<div class="card">
  <div class="hd">
    <img src="${NCC_LOGO}" alt="NCC Limited" />
    <div class="tt">NCC Limited<small>${escHtml(d.cardTitle || "IDENTITY CARD")}</small></div>
    ${d.site ? `<div class="site">${escHtml(d.site)}</div>` : ""}
  </div>
  <div class="bd">
    ${d.photo ? `<img class="ph" src="${escHtml(d.photo)}" alt="" />` : `<div class="ph-ph">${escHtml((d.name || "?")[0])}</div>`}
    <div class="info">
      <div class="nm">${escHtml(d.name)}</div>
      <div class="idno">ID: ${escHtml(d.idNo || "NOT ISSUED")}</div>
      ${rows}
    </div>
  </div>
  <div class="ft">
    ${qr ? `<img src="${qr}" alt="QR" />` : "<span></span>"}
    <div class="meta">
      <div>Gate Pass: <span class="${d.active ? "ok" : "no"}">${d.active ? "ACTIVE" : "INACTIVE"}</span></div>
      <div>Issued: ${d.issuedAt ? escHtml(new Date(d.issuedAt).toLocaleDateString()) : "—"}</div>
      <div>Status: ${escHtml((d.status || "pending").toUpperCase())}</div>
    </div>
  </div>
</div>
<script>window.addEventListener('load',function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},450);});<\/script>
</body></html>`;
  const w = window.open("", "_blank", "width=720,height=820");
  if (!w) { alert("Pop-up blocked. Please allow pop-ups for this site to print."); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export function DigitalIdCard({ d, testid = "digital-id-card" }) {
  const verifyUrl = `${window.location.origin}/workman-verify/${d.id}`;
  return (
    <>
      <div className="mx-auto max-w-sm border-4 border-ncc-navy bg-white" data-testid={testid}>
        <div className="flex items-center gap-2 border-b-4 border-ncc-gold bg-ncc-navy px-3 py-2">
          <img src={NCC_LOGO} alt="NCC Limited" className="h-8 w-auto rounded bg-white p-0.5" />
          <div className="text-[11px] font-black leading-tight text-white">NCC Limited<br /><span className="font-mono text-[9px] font-normal text-blue-200">{d.cardTitle || "IDENTITY CARD"}</span></div>
          {d.site && (
            <div className="ml-auto max-w-[110px] text-right text-[9px] font-extrabold leading-tight text-ncc-gold2" data-testid={`${testid}-site`}>
              {d.site}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-3">
          {d.photo ? (
            <img src={d.photo} alt={d.name} className="h-24 w-20 border border-slate-400 object-cover" />
          ) : (
            <div className="grid h-24 w-20 place-items-center border border-slate-400 bg-slate-100 text-2xl font-black text-slate-400">{(d.name || "?")[0]}</div>
          )}
          <div className="min-w-0 flex-1 text-[11px]">
            <div className="truncate font-heading text-sm font-black text-slate-900">{d.name}</div>
            <div className="font-mono text-slate-600">ID: {d.idNo || "NOT ISSUED"}</div>
            {d.trade && <div className="mt-1">Trade: <b>{d.trade}</b></div>}
            <div>Mob: <b>{d.mobile || "—"}</b></div>
            {d.contractorName && <div>Contractor: <b>{d.contractorName}</b></div>}
            {d.uan && <div>UAN: <b>{d.uan}</b></div>}
            {d.esic && <div>ESIC: <b>{d.esic}</b></div>}
            <div>Blood Group: <b>{d.bloodGroup || "—"}</b></div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2">
          <QRCodeCanvas value={verifyUrl} size={64} />
          <div className="text-right font-mono text-[10px] text-slate-500">
            <div>Gate Pass: <b className={d.active ? "text-emerald-700" : "text-red-600"}>{d.active ? "ACTIVE" : "INACTIVE"}</b></div>
            <div>Issued: {d.issuedAt ? new Date(d.issuedAt).toLocaleDateString() : "—"}</div>
            <div>Status: {(d.status || "pending").toUpperCase()}</div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-center">
        <button
          onClick={() => printDigitalIdCard(d)}
          data-testid={`${testid}-print`}
          className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-ncc-navy px-4 py-2 text-xs font-bold text-white hover:brightness-110"
        >
          <Printer className="h-4 w-4" /> PRINT / SAVE PDF
        </button>
      </div>
    </>
  );
}
